import type { Placement, ReferenceType } from '@floating-ui/react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useCallback, useState } from 'react';

interface UseFloatingPopupReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  x: number | null;
  y: number | null;
  reference: (node: Element | null) => void;
  floating: (node: HTMLElement | null) => void;
  strategy: 'absolute' | 'fixed';
  update: () => void;
  refs: {
    reference: { current: ReferenceType | null };
    floating: { current: HTMLElement | null };
  };
}

export const usePopup = (placement: Placement): UseFloatingPopupReturn => {
  const [isOpen, setIsOpen] = useState(false);

  const { x, y, strategy, update, refs } = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return {
    isOpen,
    open,
    close,
    toggle,
    x,
    y,
    reference: refs.setReference,
    floating: refs.setFloating,
    strategy,
    update,
    refs,
  };
};
